import React, { useState } from 'react'
import { FaChevronDown, FaChevronRight } from "react-icons/fa"
import { AiOutlineSearch, AiOutlineWarning } from "react-icons/ai"
import { Btn, COLORS, Chip, EmptyState, INPUT_STYLE, Kpi, ProgressBar, Stepper, downloadCsv, fmtShort, money, monthKey, monthLabelOf, addMonths, pct, todayIso, useNarrow } from '../FieldOpsKit'
import { REPS } from '../demoData'
import { goalOf, routesOf, salesOf, storeState, visitStats, visitsIn } from '../store'
import { UNROUTED_COUNT } from '../unrouted'
import StoreDrawer from './StoreDrawer'
import ApprovalBar from './ApprovalBar'

/** Sales Routes (admin): goals, sales, visits and compliance by route and store for any month. */
export default function RoutesTab({ ctx }) {
    const sales = ctx.state.sales
    const cur = monthKey(todayIso())
    const [month, setMonth] = useState(cur)
    const [search, setSearch] = useState('')
    const [rep, setRep] = useState('all')
    const [behindOnly, setBehindOnly] = useState(false)
    const [open, setOpen] = useState({})
    const [drawer, setDrawer] = useState(null)
    const narrow = useNarrow(900)

    const rows = (store) => {
        const goal = goalOf(sales, month, store.id, false)
        const total = salesOf(sales, month, store.id)
        const v = visitStats(visitsIn(sales, month, (x) => x.storeId === store.id))
        return { store: store, goal: goal, sales: total, visits: v, state: storeState(goal, total, month) }
    }
    const q = search.trim().toLowerCase()
    const routes = routesOf(sales).map((name) => {
        const all = sales.stores.filter((s) => s.route === name)
        const routeHit = q !== '' && name.toLowerCase().indexOf(q) >= 0
        const items = all.map(rows).filter((r) => {
            if (rep !== 'all' && r.store.rep !== rep) return false
            if (behindOnly && r.state.key !== 'behind') return false
            if (q === '' || routeHit) return true
            return r.store.name.toLowerCase().indexOf(q) >= 0 || r.store.city.toLowerCase().indexOf(q) >= 0
        })
        const t = items.reduce((a, r) => ({ goal: a.goal + (r.goal || 0), sales: a.sales + r.sales, done: a.done + r.visits.done, due: a.due + r.visits.done + r.visits.missed, planned: a.planned + r.visits.planned, met: a.met + (r.state.key === 'met' ? 1 : 0) }), { goal: 0, sales: 0, done: 0, due: 0, planned: 0, met: 0 })
        const reps = []
        items.forEach((r) => { if (reps.indexOf(r.store.rep) < 0) reps.push(r.store.rep) })
        return { name: name, items: items, totals: t, reps: reps }
    }).filter((r) => r.items.length > 0)

    const all = routes.reduce((a, r) => ({ goal: a.goal + r.totals.goal, sales: a.sales + r.totals.sales, done: a.done + r.totals.done, due: a.due + r.totals.due, planned: a.planned + r.totals.planned, met: a.met + r.totals.met, count: a.count + r.items.length }), { goal: 0, sales: 0, done: 0, due: 0, planned: 0, met: 0, count: 0 })
    const noGoal = routes.reduce((a, r) => a + r.items.filter((x) => x.goal === null).length, 0)
    const isCur = month === cur

    const exportCsv = () => {
        const out = [['Month', 'Route', 'Store', 'City', 'Salesperson', 'Goal', 'Sales', 'Goal %', 'Visits done', 'Visits planned', 'Status']]
        routes.forEach((r) => r.items.forEach((x) => out.push([month, r.name, x.store.name, x.store.city, x.store.rep, x.goal === null ? '' : x.goal, Math.round(x.sales), x.goal ? pct(x.sales, x.goal) : '', x.visits.done, x.visits.planned, x.state.label])))
        downloadCsv('sales-routes-' + month + '.csv', out)
        ctx.toast('Exported ' + (out.length - 1) + ' stores to CSV.')
    }

    const storeRow = (x) => (
        <tr key={x.store.id} style={{ cursor: 'pointer' }} onClick={() => setDrawer(x.store.id)}>
            <td><div style={{ fontWeight: 600 }}>{x.store.name}</div><div style={{ fontSize: '0.72rem', color: COLORS.slate }}>{x.store.city}</div></td>
            <td>{x.store.rep}</td>
            <td>{x.goal === null ? <Chip tone="gray">No goal</Chip> : money(x.goal)}</td>
            <td>{money(x.sales)}</td>
            <td style={{ minWidth: 140 }}>{x.goal ? <ProgressBar value={x.sales} max={x.goal} /> : '—'}</td>
            <td>{x.visits.done} / {x.visits.planned}</td>
            <td><Chip tone={x.state.tone}>{x.state.label}</Chip></td>
        </tr>
    )
    const storeCard = (x) => (
        <div key={x.store.id} onClick={() => setDrawer(x.store.id)} style={{ background: '#FFFFFF', border: '1px solid ' + COLORS.line, borderRadius: 10, padding: '10px 12px', marginBottom: 8, cursor: 'pointer' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                <div><div style={{ fontWeight: 700, color: COLORS.navy }}>{x.store.name}</div><div style={{ fontSize: '0.72rem', color: COLORS.slate }}>{x.store.city} · {x.store.rep}</div></div>
                <Chip tone={x.state.tone}>{x.state.label}</Chip>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.78rem', margin: '6px 0 4px', color: COLORS.slate }}>
                <span>Goal {x.goal === null ? '—' : money(x.goal)}</span><span>Sales {money(x.sales)}</span><span>Visits {x.visits.done}/{x.visits.planned}</span>
            </div>
            {x.goal ? <ProgressBar value={x.sales} max={x.goal} height={6} /> : null}
        </div>
    )

    return (
        <React.Fragment>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10, marginBottom: 12 }}>
                <Stepper dark label={monthLabelOf(month)} onPrev={() => setMonth(addMonths(month, -1))} onNext={() => setMonth(addMonths(month, 1))} canPrev={month > addMonths(cur, -2)} canNext={month < addMonths(cur, 1)} onToday={month !== cur ? () => setMonth(cur) : null} />
                <Btn kind="secondary" onClick={exportCsv}>Export CSV</Btn>
            </div>

            <ApprovalBar ctx={ctx} month={month} compact />

            <div className="row g-3 mb-3">
                <div className="col-6 col-lg-3"><Kpi label="Monthly goal" value={money(all.goal)} sub={routes.length + ' routes · ' + all.count + ' stores'} /></div>
                <div className="col-6 col-lg-3"><Kpi label="Sales" value={money(all.sales)} sub={pct(all.sales, all.goal) + '% of the goal'} status={pct(all.sales, all.goal) >= 100 ? 'success' : pct(all.sales, all.goal) >= 60 ? 'primary' : 'warning'} /></div>
                <div className="col-6 col-lg-3"><Kpi label="Visits done" value={all.done + ' / ' + all.planned} sub={pct(all.done, all.due) + '% of the visits due'} status={pct(all.done, all.due) >= 75 ? 'success' : 'warning'} /></div>
                <div className="col-6 col-lg-3"><Kpi label="Stores at goal" value={all.met + ' of ' + all.count} sub={noGoal > 0 ? noGoal + ' without goal' : pct(all.met, all.count) + '% of stores'} status={pct(all.met, all.count) >= 50 ? 'success' : 'danger'} /></div>
            </div>

            <div className="card mb-3" style={{ borderLeft: '4px solid ' + COLORS.amber }}>
                <div className="card-body" style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 20px' }}>
                    <AiOutlineWarning style={{ color: COLORS.amber, fontSize: '1.3rem', flexShrink: 0 }} />
                    <div style={{ fontSize: '0.83rem', color: COLORS.navy }}><strong>{UNROUTED_COUNT} Altitude stores have no route in Sage ("SinRuta").</strong> They cannot get a goal or an agenda until a route is assigned — see the "Unrouted stores" tab.</div>
                </div>
            </div>

            <div className="row g-2 mb-3 align-items-center">
                <div className="col-12 col-md-5"><div style={{ position: 'relative' }}>
                    <AiOutlineSearch style={{ position: 'absolute', left: 12, top: 11, color: COLORS.muted }} />
                    <input type="text" placeholder="Search route, store or city" value={search} onChange={(e) => setSearch(e.target.value)} style={{ ...INPUT_STYLE, width: '100%', paddingLeft: 34 }} />
                </div></div>
                <div className="col-6 col-md-3">
                    <select value={rep} onChange={(e) => setRep(e.target.value)} style={{ ...INPUT_STYLE, width: '100%' }} aria-label="Salesperson">
                        <option value="all">All salespeople</option>
                        {REPS.map((r) => <option key={r} value={r}>{r}</option>)}
                    </select>
                </div>
                <div className="col-6 col-md-4"><label style={{ fontSize: '0.83rem', display: 'flex', alignItems: 'center', gap: 8, margin: 0, cursor: 'pointer' }}>
                    <input type="checkbox" checked={behindOnly} onChange={(e) => setBehindOnly(e.target.checked)} /> Only stores behind goal
                </label></div>
            </div>

            {routes.length === 0 ? <EmptyState title="No routes match" text="Change the search or filters." action={<Btn kind="secondary" onClick={() => { setSearch(''); setRep('all'); setBehindOnly(false) }}>Clear filters</Btn>} /> : (
                narrow ? routes.map((r) => {
                    const isOpen = q !== '' || open[r.name]
                    return (
                        <div key={r.name} className="card mb-2"><div className="card-body" style={{ padding: '12px 14px' }}>
                            <div onClick={() => setOpen({ ...open, [r.name]: !open[r.name] })} style={{ cursor: 'pointer' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <strong style={{ color: COLORS.navy }}>{isOpen ? <FaChevronDown /> : <FaChevronRight />} {r.name}</strong>
                                    <span style={{ fontSize: '0.75rem', color: COLORS.slate }}>{r.reps.join(' · ')} · {r.items.length} stores</span>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.78rem', margin: '6px 0 4px', color: COLORS.slate }}>
                                    <span>Goal {money(r.totals.goal)}</span><span>Sales {money(r.totals.sales)}</span><span>Visits {r.totals.done}/{r.totals.planned}</span>
                                </div>
                                <ProgressBar value={r.totals.sales} max={r.totals.goal} />
                            </div>
                            {isOpen ? <div style={{ marginTop: 10 }}>{r.items.map(storeCard)}</div> : null}
                        </div></div>
                    )
                }) : (
                    <div className="tableFixHeadSmall">
                        <table className="table table-clean">
                            <thead><tr><th style={{ width: 28 }}></th><th>Route</th><th>Salespeople</th><th>Stores</th><th>Goal</th><th>Sales</th><th>Goal progress</th><th>Visits</th><th>At goal</th></tr></thead>
                            <tbody>
                                {routes.map((r) => {
                                    const p = pct(r.totals.sales, r.totals.goal)
                                    const isOpen = q !== '' || open[r.name]
                                    return (
                                        <React.Fragment key={r.name}>
                                            <tr className={p >= 100 ? 'greenTableCell' : p >= 30 ? 'yellowTableCell' : 'grayTableCell'} style={{ cursor: 'pointer' }} onClick={() => setOpen({ ...open, [r.name]: !open[r.name] })}>
                                                <td>{isOpen ? <FaChevronDown /> : <FaChevronRight />}</td>
                                                <td style={{ fontWeight: 700 }}>{r.name}</td>
                                                <td>{r.reps.join(' · ')}</td>
                                                <td>{r.items.length}</td>
                                                <td>{money(r.totals.goal)}</td>
                                                <td>{money(r.totals.sales)}</td>
                                                <td style={{ minWidth: 160 }}><ProgressBar value={r.totals.sales} max={r.totals.goal} /></td>
                                                <td style={{ minWidth: 130 }}><div style={{ fontSize: '0.72rem', color: COLORS.slate }}>{r.totals.done} / {r.totals.planned}</div><ProgressBar value={r.totals.done} max={r.totals.planned} height={6} hideLabel /></td>
                                                <td>{r.totals.met} of {r.items.length}</td>
                                            </tr>
                                            {isOpen ? (
                                                <tr><td></td><td colSpan={8} style={{ background: COLORS.bg, padding: '8px 12px 14px' }}>
                                                    <table className="table table-clean" style={{ marginBottom: 0 }}>
                                                        <thead><tr><th>Store</th><th>Rep</th><th>Goal</th><th>Sales</th><th>Progress</th><th>Visits</th><th>Status</th></tr></thead>
                                                        <tbody>{r.items.map(storeRow)}</tbody>
                                                    </table>
                                                </td></tr>
                                            ) : null}
                                        </React.Fragment>
                                    )
                                })}
                            </tbody>
                            <tfoot className="tfoot"><tr>
                                <td></td><td style={{ fontWeight: 800 }}>Total</td><td></td><td>{all.count}</td><td>{money(all.goal)}</td><td>{money(all.sales)}</td>
                                <td>{pct(all.sales, all.goal)}%</td><td>{all.done} / {all.planned}</td><td>{all.met} of {all.count}</td>
                            </tr></tfoot>
                        </table>
                    </div>
                )
            )}
            {isCur ? <div style={{ fontSize: '0.72rem', color: COLORS.muted, fontStyle: 'italic', marginTop: 8 }}>* Current month figures are up to {fmtShort(todayIso())}. A store is "On track" when it is at 90 % or more of the pace expected for this point of the month.</div> : null}
            {drawer ? <StoreDrawer ctx={ctx} storeId={drawer} onClose={() => setDrawer(null)} /> : null}
        </React.Fragment>
    )
}
